'use client'

import { C, Callout, Exercise, H, Kbd, Lesson, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/your-workbench'

export default function YourWorkbench() {
  return (
    <Lesson>
      <Step>
        <H>A computer that does what you write</H>
        <P>
          Throughout this course you’ll write small programs in <strong>TypeScript</strong> — the language behind a huge share of
          the web. It runs right here in your browser: no installs, no accounts, nothing to configure.
        </P>
        <P>
          Below is your first program. It has one instruction: <C>console.log</C>, which means “print this”. Press{' '}
          <strong>Run</strong> (or <Kbd>⌘</Kbd>/<Kbd>Ctrl</Kbd> + <Kbd>Enter</Kbd> while the editor is focused).
        </P>
        <Exercise
          id="hello"
          title="Your first program" {...EX.hello}
        />
        <P>
          Now change the text between the quotes to anything you like and run it again. You can’t break anything here — and if
          you get lost, <strong>Reset</strong> brings back the original.
        </P>
      </Step>

      <Step>
        <H>It’s a calculator, too</H>
        <P>
          Computers are, at heart, very fast calculators. Try the program below. The lines starting with <C>{'//'}</C> are{' '}
          <strong>comments</strong>: notes for humans that the computer ignores.
        </P>
        <Exercise
          id="calc"
          title="Arithmetic" {...EX.calc}
        />
        <Callout kind="try">Add your own line that prints how many seconds are in a day (60 × 60 × 24).</Callout>
      </Step>

      <Step>
        <H>Pictures, not just numbers</H>
        <P>
          Machine learning is much easier to understand when you can <em>see</em> it. The editor comes with a built-in{' '}
          <C>plot</C> helper. Here it draws the curve you get by multiplying each number by itself:
        </P>
        <Exercise
          id="first-plot"
          title="Your first plot" {...EX.firstPlot}
        />
        <P>
          That second shape — a line that drops quickly, then flattens out — is the most important picture in machine learning.
          It’s what <em>training</em> looks like. You’ll see it hundreds of times.
        </P>
      </Step>

      <Step>
        <H>Your editor helps you</H>
        <P>This isn’t a plain text box. It understands your code as you type:</P>
        <ul className="gd-list">
          <li>
            <strong>Autocomplete.</strong> Type <C>plot.</C> (with the dot) and a list of everything <C>plot</C> can do pops up.
            Hover over any name to see what it does.
          </li>
          <li>
            <strong>Mistakes are underlined</strong> in red before you even run. Hover the squiggle to read why.
          </li>
          <li>
            <strong>Errors point to the line.</strong> If your program crashes, the output tells you what went wrong and the
            editor marks the line.
          </li>
        </ul>
        <Exercise
          id="broken"
          title="Spot the problem" {...EX.broken}
        />
        <Callout kind="note">
          There is deliberately <strong>no AI assistant</strong> in this editor. The point is for <em>your</em> brain to do the
          learning — the struggle is where it happens.
        </Callout>
      </Step>

      <Step gate="answer-42">
        <H>Your first challenge</H>
        <P>
          Challenges have a <strong>Check</strong> button. It runs your code, then runs a few hidden checks against it and tells
          you which pass. Make the checks pass to continue.
        </P>
        <Exercise
          id="answer-42"
          title="Make it 42" {...EX.answer42}
          hints={[<>The checks look at a value called <C>answer</C>. What would you multiply 7 by to get 42?</>]}
        />
      </Step>

      <Step gate="q-run">
        <H>Ready</H>
        <P>That’s the whole toolkit: write, run, read the output, check. One quick question and you’re through.</P>
        <Quiz
          id="q-run"
          question="What’s the difference between Run and Check?"
          options={[
            { label: 'Run executes your code; Check also tests it against the challenge.', correct: true },
            { label: 'Run saves your code; Check executes it.', why: 'Your code saves automatically as you type. Both buttons execute it.' },
            { label: 'There is no difference.', why: 'Check runs extra hidden tests after your code to see if you solved the challenge.' },
          ]}
          explain="Use Run freely while you experiment; press Check when you think you’ve solved it."
        />
        <P>
          Next, we’ll learn just enough TypeScript to build every model in this course ourselves. If you already program, the
          next module will go quickly — feel free to skim.
        </P>
      </Step>
    </Lesson>
  )
}
